import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import Asterisk01Icon from "@redotech/redo-icons/asterisk-01_ai-gradient.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ConversationPlatform,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { ConversationFiltersV3 } from "@redotech/redo-model/conversation-filters/conversation-filters";
import {
  getCustomerDisplayName,
  getPrimaryCustomerEmail,
  getPrimaryCustomerFacebookName,
  getPrimaryCustomerInstagramUsername,
  getPrimaryCustomerPhoneNumber,
} from "@redotech/redo-model/customer";
import { GetUser as User } from "@redotech/redo-model/user";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import { getConversations, mergeConversations } from "../client/conversations";
import { channelDisplayName } from "../utils";
import { ConversationListItem } from "./conversation-list-item";
import { DetailedConversationCard } from "./detailed-conversation-card";
import * as mergeModalCss from "./merge-modal.module.css";

export const MergeModal = memo(function MergeModal({
  open,
  setOpen,
  conversations,
  setActiveConversation,
  suggestedConversations = [],
  setConversationsInProximity,
  selectAllInfo,
  onBack,
  cleanupFn,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  suggestedConversations: MinimalConversation[];
  setActiveConversation: (val: MinimalConversation) => void;
  setConversationsInProximity?: Dispatch<SetStateAction<MinimalConversation[]>>;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    filters: ConversationFiltersV3;
    setBlockRefresh: (val: boolean) => void;
  };
  /** Called when merge modal would return from details step to tickets step.
   * If callback returns false, merge modal will not change step. See MergeSuggestionFlow
   * for an example usage. */
  onBack?: () => void;
  cleanupFn?: () => void;
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [step, setStep] = useState<"tickets" | "details">("tickets");
  const [searchString, setSearchString] = useState<string>("");
  const [pending, setPending] = useState<boolean>(false);
  const debouncedSearchString = useDebounce(searchString, 500);
  const [conversationsLoading, setConversationsLoading] =
    useState<boolean>(false);
  const [searchedConversations, setSearchedConversations] = useState<
    MinimalConversation[]
  >([]);
  const [selectedConversations, setSelectedConversations] =
    useState<MinimalConversation[]>(conversations);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(
    suggestedConversations.length > 0,
  );
  const isInitialLoad = useRef(true);

  const doSelectAllLoad = async () => {
    if (!selectAllInfo) {
      return;
    }

    if (isInitialLoad.current) {
      setPending(true);
      isInitialLoad.current = false;
    }

    try {
      const response = await getConversations(client, {
        pageContinue: undefined,
        pageSize: 100,
        filters: selectAllInfo.filters,
      });
      const conversations = response.data;
      const selectedConversations = conversations.filter(
        (conversation) =>
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ) &&
          ![
            ConversationPlatform.INSTAGRAM_COMMENTS,
            ConversationPlatform.FACEBOOK_COMMENTS,
          ].includes(conversation.platform),
      );
      setSelectedConversations(selectedConversations);
      return;
    } catch (error) {
      console.error("Error loading conversations:", error);
    } finally {
      setPending(false);
    }
    return;
  };

  const handleConversationToggle = useCallback(
    (conversation: MinimalConversation) => {
      const isAlreadySelected = selectedConversations.some(
        (selected) => selected._id === conversation._id,
      );

      if (isAlreadySelected) {
        setSelectedConversations(
          selectedConversations.filter(
            (selected) => selected._id !== conversation._id,
          ),
        );
      } else {
        setSelectedConversations([...selectedConversations, conversation]);
      }
    },
    [selectedConversations],
  );

  const onInputChange = (value: string) => {
    setSearchString(value);
  };

  const [_, doConversationsLoad] = useTriggerLoad(async () => {
    const { data } = await getConversations(client, {
      pageContinue: undefined,
      pageSize: 20,
      filters: {
        advancedFilters: [],
        search: debouncedSearchString || undefined,
      },
    });
    setConversationsLoading(false);
    const searchResults = data.filter((conversation) => {
      return conversations.every(
        (conversation_) => conversation_._id !== conversation._id,
      );
    });
    setSearchedConversations(searchResults);
    return data;
  });

  useEffect(() => {
    if (searchString !== undefined) {
      setConversationsLoading(true);
      doConversationsLoad();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchString]);

  useEffect(() => {
    if (conversations.length > 1 || selectAllInfo) {
      setStep("details");
    }
  }, [conversations, selectAllInfo]);

  useEffect(() => {
    if (!selectAllInfo || !open || pending) {
      return;
    }
    void doSelectAllLoad();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAllInfo, open]);

  useEffect(() => {
    const validSelectedConversations = selectedConversations.filter(
      (conversation) =>
        ![
          ConversationPlatform.INSTAGRAM_COMMENTS,
          ConversationPlatform.FACEBOOK_COMMENTS,
        ].includes(conversation.platform),
    );
    if (validSelectedConversations.length !== selectedConversations.length) {
      setSelectedConversations(validSelectedConversations);
    }
  }, [selectedConversations]);

  const getTicketCount = () => {
    return (
      (selectAllInfo?.count ?? 0) -
        (selectAllInfo?.deselectedConversations?.length ?? 0) ||
      selectedConversations.length
    );
  };

  const conversationOptions: RedoListItem<string>[] = useMemo(() => {
    const conversationsToShow = showSuggestions
      ? suggestedConversations
      : searchedConversations;

    return conversationsToShow
      .filter(
        (conversation) =>
          // Due to the special structure of Instagram and Facebook comments conversations, we don't allow merging them
          ![
            ConversationPlatform.INSTAGRAM_COMMENTS,
            ConversationPlatform.FACEBOOK_COMMENTS,
          ].includes(conversation.platform),
      )
      .map<RedoListItem<string>>((conversation) => {
        const isSelected = !!selectedConversations.some(
          (selected) => selected._id === conversation._id,
        );

        return {
          id: conversation._id,
          value: conversation._id,
          type: "custom",
          text: getCustomerDisplayName(conversation.customer),
          render: (
            <ConversationListItem
              conversation={conversation}
              isSelected={isSelected}
              onToggle={() => handleConversationToggle(conversation)}
            />
          ),
          useWrapper: false,
          children: [],
        };
      });
  }, [
    searchedConversations,
    suggestedConversations,
    selectedConversations,
    handleConversationToggle,
    showSuggestions,
  ]);

  const selectedConversationIds = selectedConversations.map((conv) => conv._id);
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const ticketStep = (
    <div>
      <Text fontSize="sm" mb="md" ml="sm">
        Search tickets to merge with
      </Text>
      <Flex dir="column" gap="lg" mx="sm">
        <Flex dir="row" gap="md" justify="space-between">
          <RedoTextInput
            fullWidth
            placeholder={
              showSuggestions
                ? "Showing suggested tickets to merge"
                : "Search for tickets to merge..."
            }
            ref={setInputRef}
            setValue={onInputChange}
            size="md"
            state={showSuggestions ? "readonly" : "default"}
            value={showSuggestions ? "" : searchString || ""}
          />
          {suggestedConversations.length > 0 && (
            <Flex align="center" gap="sm" style={{ minWidth: "fit-content" }}>
              <RedoToggle
                setValue={() => {
                  setShowSuggestions((prev) => !prev);
                }}
                size="sm"
                value={showSuggestions}
              />
              <Asterisk01Icon />
              <Text fontSize="md" textColor="secondary">
                Suggest tickets to merge
              </Text>
            </Flex>
          )}
        </Flex>
        <div className={mergeModalCss.listContainer}>
          <RedoList
            focusedIndex={focusedIndex}
            items={conversationOptions}
            itemSelected={({ item }) => {
              const conversation = searchedConversations.find(
                (conv) => conv._id === item.value,
              );
              if (conversation) {
                handleConversationToggle(conversation);
              }
            }}
            itemsLoading={showSuggestions ? false : conversationsLoading}
            refToListenTo={inputRef}
            selectedItems={selectedConversationIds}
            setFocusedIndex={setFocusedIndex}
            size="sm"
          />
        </div>
      </Flex>
    </div>
  );

  if (step === "details" && !pending) {
    return (
      <DetailsStep
        cleanupFn={cleanupFn}
        onBack={onBack}
        selectedConversations={selectedConversations}
        setActiveConversation={setActiveConversation}
        setConversationsInProximity={setConversationsInProximity}
        setOpen={setOpen}
        setPending={setPending}
        setStep={setStep}
      />
    );
  }

  return (
    <RedoModal
      isOpen={open}
      modalSize="lg"
      onModalCloseRequested={() => setOpen(false)}
      primaryButton={
        step === "tickets" && !pending
          ? { text: "Continue", onClick: () => setStep("details") }
          : undefined
      }
      secondaryButton={
        step === "tickets" && !pending
          ? { text: "Cancel", onClick: () => setOpen(false) }
          : undefined
      }
      theme="neutral"
      title={
        step === "tickets"
          ? "Select ticket(s) to merge with"
          : `Merge ${getTicketCount()} tickets`
      }
      TitleIcon={CheckCircleIcon}
    >
      <div className={mergeModalCss.modalContent}>
        {pending ? (
          <div className={mergeModalCss.animationContainer}>
            <LoadingRedoAnimation />
          </div>
        ) : (
          step === "tickets" && ticketStep
        )}
      </div>
    </RedoModal>
  );
});

interface CustomerOption {
  id: string;
  value: string;
}
export const DetailsStep = memo(function DetailsStep({
  selectedConversations,
  setOpen,
  setStep,
  setActiveConversation,
  setConversationsInProximity,
  setPending,
  onBack,
  cleanupFn,
}: {
  selectedConversations: MinimalConversation[];
  setOpen: (val: boolean) => void;
  setStep: (val: "tickets" | "details") => void;
  setActiveConversation: (val: MinimalConversation) => void;
  setConversationsInProximity?: Dispatch<SetStateAction<MinimalConversation[]>>;
  setPending: (val: boolean) => void;
  onBack?: () => void;
  cleanupFn?: () => void;
}) {
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const users = team.users;
  const [possibleCustomers, setPossibleCustomers] = useState<CustomerOption[]>(
    [],
  );
  const [selectedCustomer, setSelectedCustomer] = useState<
    CustomerOption | undefined
  >(undefined);
  type PossibleCommChannels = Exclude<
    ConversationPlatform,
    | ConversationPlatform.INSTAGRAM_COMMENTS
    | ConversationPlatform.FACEBOOK_COMMENTS
  >;
  const [possibleCommChannels, setPossibleCommChannels] = useState<
    PossibleCommChannels[]
  >([]);
  const [selectedCommChannel, setSelectedCommChannel] = useState<
    PossibleCommChannels | undefined
  >(undefined);
  const [possibleSubjects, setPossibleSubjects] = useState<string[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string | undefined>(
    undefined,
  );
  const [assignee, setAssignee] = useState<User | null | undefined>(
    selectedConversations[0]?.assignee || null,
  );
  const [currentTicket] = useState<MinimalConversation>(
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    selectedConversations[0],
  );
  const [ticketsToMerge, setTicketsToMerge] = useState<MinimalConversation[]>(
    selectedConversations.slice(1),
  );

  const handleTicketToMergeToggle = (conversation: MinimalConversation) => {
    const isSelected = ticketsToMerge.some(
      (ticket) => ticket._id === conversation._id,
    );

    if (isSelected) {
      setTicketsToMerge(
        ticketsToMerge.filter((ticket) => ticket._id !== conversation._id),
      );
    } else {
      setTicketsToMerge([...ticketsToMerge, conversation]);
    }
  };

  const handleMergeTickets = async () => {
    if (!selectedCustomer || !selectedCommChannel || assignee === undefined) {
      return;
    }
    setPending(true);
    const conversationsToMerge = [currentTicket, ...ticketsToMerge];
    const selectedConversationIds = conversationsToMerge.map(
      (conversation) => conversation._id,
    );
    // all but primary conversation remove from proximity
    setConversationsInProximity?.((prev: MinimalConversation[]) =>
      prev.filter(
        (conv) => !selectedConversationIds.slice(1).includes(conv._id),
      ),
    );
    const mergedConversation = await mergeConversations(client, {
      selectedConversationIds,
      customer: selectedCustomer?.id,
      platform: selectedCommChannel,
      subject: selectedSubject || "",
      assignee: assignee?._id || null,
    });
    const channels = [
      ...new Set(
        conversationsToMerge.map((conversation) => conversation.platform),
      ),
    ];
    amplitude.logEvent("merge-conversation", {
      conversationIds: selectedConversationIds,
      channels,
    });
    setActiveConversation(mergedConversation);
    setPending(false);
    setOpen(false);
    cleanupFn?.();
  };

  useEffect(() => {
    const customers: CustomerOption[] = [];
    for (const conversation of selectedConversations) {
      const primaryCustomerEmail = getPrimaryCustomerEmail(
        conversation.customer,
      );
      const primaryCustomerPhoneNumber = getPrimaryCustomerPhoneNumber(
        conversation.customer,
      );
      const primaryCustomerFacebookName = getPrimaryCustomerFacebookName(
        conversation.customer,
      );
      const primaryCustomerInstagramName = getPrimaryCustomerInstagramUsername(
        conversation.customer,
      );
      if (
        conversation.platform === ConversationPlatform.EMAIL &&
        primaryCustomerEmail
      ) {
        customers.push({
          id: conversation.customer?.customer,
          value: `${
            conversation.customer?.name || "Unknown Customer"
          } (${primaryCustomerEmail})`,
        });
      } else
        switch (conversation.platform) {
          case ConversationPlatform.FACEBOOK: {
            customers.push({
              id: conversation.customer?.customer,
              value: `${
                conversation.customer?.name || "Unknown Customer"
              } (${primaryCustomerFacebookName} on FB)`,
            });

            break;
          }
          case ConversationPlatform.INSTAGRAM: {
            customers.push({
              id: conversation.customer?.customer,
              value: `${
                conversation.customer?.name || "Unknown Customer"
              } (${primaryCustomerInstagramName} on IG)`,
            });

            break;
          }
          case ConversationPlatform.SMS: {
            customers.push({
              id: conversation.customer?.customer,
              value: `${
                conversation.customer?.name || "Unknown Customer"
              } (${primaryCustomerPhoneNumber})`,
            });

            break;
          }
          default: {
            customers.push({
              id: conversation.customer?.customer,
              value: `${conversation.customer?.name || "Unknown Customer"}`,
            });
          }
        }
    }
    setPossibleCustomers(customers);
    setSelectedCustomer(customers[0]);
    const commChannels: PossibleCommChannels[] = [];
    for (const conversation of selectedConversations) {
      if (
        conversation.platform &&
        conversation.platform !== ConversationPlatform.INSTAGRAM_COMMENTS &&
        conversation.platform !== ConversationPlatform.FACEBOOK_COMMENTS &&
        !commChannels.includes(conversation.platform)
      ) {
        commChannels.push(conversation.platform);
      }
    }
    setPossibleCommChannels(commChannels);
    setSelectedCommChannel(commChannels[0]);
    const subjects: string[] = [];
    for (const conversation of selectedConversations) {
      if (conversation.platform === ConversationPlatform.EMAIL) {
        subjects.push(conversation.subject);
      }
    }
    setPossibleSubjects(subjects);
    setSelectedSubject(subjects[0]);
  }, [selectedConversations]);

  return (
    <RedoModal
      isOpen
      modalSize="lg"
      onModalCloseRequested={() => setOpen(false)}
      primaryButton={{
        text: "Merge tickets",
        onClick: handleMergeTickets,
        disabled:
          ticketsToMerge.length === 0 ||
          !selectedCustomer?.id ||
          !selectedCommChannel ||
          assignee === undefined,
      }}
      secondaryButton={{
        text: "Back",
        onClick: () => {
          const shouldChangeStep = onBack?.() ?? true;
          if (shouldChangeStep) {
            setStep("tickets");
          }
        },
      }}
      theme="neutral"
      title={`Merge ${ticketsToMerge.length + 1} tickets`}
      TitleIcon={CheckCircleIcon}
    >
      <div className={mergeModalCss.detailsContent}>
        <Flex dir="column" gap="sm">
          <Text fontSize="md" fontWeight="medium">
            Current ticket
          </Text>
          <DetailedConversationCard conversation={currentTicket} />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="md" fontWeight="medium">
            Tickets being merged
          </Text>
          <Flex dir="column" gap="md">
            {selectedConversations.slice(1).map((conversation) => {
              const isSelected = ticketsToMerge.some(
                (ticket) => ticket._id === conversation._id,
              );
              return (
                <DetailedConversationCard
                  conversation={conversation}
                  isSelected={isSelected}
                  key={conversation._id}
                  onToggle={() => handleTicketToMergeToggle(conversation)}
                  showCheckbox
                />
              );
            })}
          </Flex>
        </Flex>

        <Flex dir="column" gap="md">
          <LabeledInput label="Customer">
            <SelectDropdown
              options={possibleCustomers}
              placeholder="Select customer"
              value={selectedCustomer}
              valueChange={(e: CustomerOption) => {
                setSelectedCustomer(e);
              }}
            >
              {(option) => option.value}
            </SelectDropdown>
          </LabeledInput>

          <LabeledInput label="Communication channel">
            <SelectDropdown
              options={possibleCommChannels}
              placeholder="Select communication channel"
              value={selectedCommChannel}
              valueChange={(e: PossibleCommChannels) => {
                setSelectedCommChannel(e);
              }}
            >
              {(option) => channelDisplayName(option)}
            </SelectDropdown>
          </LabeledInput>

          {selectedCommChannel === ConversationPlatform.EMAIL && (
            <LabeledInput label="Subject">
              <SelectDropdown
                options={possibleSubjects}
                placeholder="Select subject"
                value={selectedSubject}
                valueChange={(e: string) => {
                  setSelectedSubject(e);
                }}
              >
                {(option) => option}
              </SelectDropdown>
            </LabeledInput>
          )}

          <LabeledInput label="Assignee">
            <SelectDropdown
              allowNull
              options={[null, ...users.map((user) => user.user as User)]}
              placeholder="Assignee"
              value={assignee}
              valueChange={setAssignee}
            >
              {(option) => {
                if (option) {
                  return `${(option as User)?.name}`;
                } else {
                  return "Unassigned";
                }
              }}
            </SelectDropdown>
          </LabeledInput>
        </Flex>
      </div>
    </RedoModal>
  );
});
